import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Trash2, RefreshCw, Clock, Globe, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WebhookTester() {
  const navigate = useNavigate();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookId, setWebhookId] = useState("");
  const [requests, setRequests] = useState([]);
  const [mockResponse, setMockResponse] = useState("{\n  \"status\": \"success\"\n}");
  const [mockStatus, setMockStatus] = useState(200);
  const [loading, setLoading] = useState(false);

  const generateWebhook = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/webhooks/generate`);
      // Use webhook_url from backend response (custom domain)
      const url = response.data.webhook_url || `${BACKEND_URL}/api/webhooks/receive/${response.data.webhook_id}`;
      setWebhookUrl(url);
      setWebhookId(response.data.webhook_id);
      toast.success("Webhook URL generated!");
    } catch (error) {
      toast.error("Failed to generate webhook");
    }
    setLoading(false);
  };

  const fetchRequests = async () => {
    if (!webhookId) return;
    try {
      const response = await axios.get(`${API}/webhooks/${webhookId}/requests`);
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch requests", error);
    }
  };

  const updateMockResponse = async () => {
    if (!webhookId) return;
    try {
      await axios.put(`${API}/webhooks/${webhookId}/mock-response`, {
        response: JSON.parse(mockResponse),
        status_code: parseInt(mockStatus)
      });
      toast.success("Mock response updated!");
    } catch (error) {
      toast.error("Failed to update mock response");
    }
  };

  const deleteWebhook = async () => {
    if (!webhookId) return;
    try {
      await axios.delete(`${API}/webhooks/${webhookId}`);
      setWebhookUrl("");
      setWebhookId("");
      setRequests([]);
      toast.success("Webhook deleted");
    } catch (error) {
      toast.error("Failed to delete webhook");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  useEffect(() => {
    if (webhookId) {
      const interval = setInterval(fetchRequests, 2000);
      return () => clearInterval(interval);
    }
  }, [webhookId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      {/* Header */}
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                data-testid="back-home-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Webhook Tester</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Generate Section */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Generate Webhook URL</h2>
          <p className="text-zinc-400 mb-4">Create a unique endpoint to receive webhook requests (expires in 24 hours)</p>
          
          {!webhookUrl ? (
            <Button
              data-testid="generate-webhook-btn"
              onClick={generateWebhook}
              disabled={loading}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
            >
              {loading ? "Generating..." : "Generate Webhook URL"}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Input
                  data-testid="webhook-url-input"
                  value={webhookUrl}
                  readOnly
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                />
                <Button
                  data-testid="copy-url-btn"
                  onClick={() => copyToClipboard(webhookUrl)}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="delete-webhook-btn"
                  onClick={deleteWebhook}
                  variant="outline"
                  className="border-red-900 text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center space-x-4 text-sm text-zinc-400">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Expires in 24 hours</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4" />
                  <span>Accepts all HTTP methods</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {webhookId && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Mock Response */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Mock Response</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Response Body (JSON)</label>
                  <Textarea
                    data-testid="mock-response-textarea"
                    value={mockResponse}
                    onChange={(e) => setMockResponse(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[200px]"
                    placeholder='{"status": "success"}'
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Status Code</label>
                  <Input
                    data-testid="mock-status-input"
                    type="number"
                    value={mockStatus}
                    onChange={(e) => setMockStatus(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <Button
                  data-testid="update-mock-btn"
                  onClick={updateMockResponse}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                >
                  Update Mock Response
                </Button>
              </div>
            </Card>

            {/* Requests */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Received Requests ({requests.length})</h3>
                <Button
                  data-testid="refresh-requests-btn"
                  onClick={fetchRequests}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {requests.length === 0 ? (
                  <div className="text-center py-12">
                    <Code className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500">No requests received yet</p>
                    <p className="text-sm text-zinc-600 mt-1">Send a request to your webhook URL</p>
                  </div>
                ) : (
                  requests.map((req, index) => (
                    <Card
                      key={req.id}
                      data-testid={`request-item-${index}`}
                      className="bg-zinc-800/50 border-zinc-700 p-4 hover:bg-zinc-800 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-cyan-400">{req.method}</span>
                        <span className="text-xs text-zinc-500">
                          {new Date(req.received_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <Tabs defaultValue="body" className="w-full">
                        <TabsList className="bg-zinc-900 border-zinc-700">
                          <TabsTrigger value="body">Body</TabsTrigger>
                          <TabsTrigger value="headers">Headers</TabsTrigger>
                        </TabsList>
                        <TabsContent value="body">
                          <pre className="text-xs text-zinc-300 overflow-x-auto max-h-32">
                            {typeof req.body === 'object' ? JSON.stringify(req.body, null, 2) : req.body || 'Empty'}
                          </pre>
                        </TabsContent>
                        <TabsContent value="headers">
                          <pre className="text-xs text-zinc-300 overflow-x-auto max-h-32">
                            {JSON.stringify(req.headers, null, 2)}
                          </pre>
                        </TabsContent>
                      </Tabs>
                    </Card>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}